import {
  AddIcon,
  PhoneIcon,
  SearchIcon,
  WarningTwoIcon,
} from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { useState } from 'react';
import { PiCardsBold, PiTableBold } from 'react-icons/pi';
import { getSearchResults } from '../../services';
import { SearchResults } from '../../types';
import { analytics } from '../../utils/analytics';
import { SearchResultsCardView } from './SearchResultsCardView';
import { SearchResultsTable } from './SearchResultsTable';
const ResourceMatrixSearch = () => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [practice, setPractice] = useState('');
  const [searchResults, setSearchResults] =
    useState<SearchResults | null>(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [isCardView, setIsCardView] = useState(false);
  const [searchAttempted, setSearchAttempted] = useState(false);
  const toast = useToast();
  const searchTips =
    'Try checking your spelling, using more general keywords, or adjusting the filters.';

  const handleSearch = async () => {
    if (name || phone || practice) {
      setSearchAttempted(true);
      setResultsLoading(true);
      try {
        const { data, loading } = await getSearchResults(
          name,
          phone,
          practice
        );
        setResultsLoading(loading);

        const hasResults = Object.values(data).some(
          (value) => value && Object.keys(value).length > 0
        );

        if (hasResults) {
          setSearchResults(data);
          analytics.trackEvent('search', 'global', {
            resultCount: Object.keys(data.file).length,
          });

          toast({
            title: 'Search Successful',
            description: `${
              Object.keys(data.file).length
            } search results fetched successfully!`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        } else {
          setSearchResults(null);
          toast({
            title: 'No Results Found',
            description: 'No results found for the search criteria.',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (error) {
        setResultsLoading(false);
        toast({
          title: 'Search Failed',
          description:
            'Failed to fetch search results. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } else {
      toast({
        title: 'Search Validation',
        description:
          'Please fill in the input fields to perform the search.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setName('');
    setPhone('');
    setPractice('');
    setSearchResults(null);
    setSearchAttempted(false);
  };
  const toggleView = () => {
    setIsCardView(!isCardView); // Toggle between card and table view
  };

  return (
    <>
      <Box>
        <Heading as="h2" size="lg" mb={4}>
          Resource Matrix
        </Heading>
        <Flex
          direction={{ base: 'column', lg: 'row' }}
          wrap={{ base: 'wrap', lg: 'nowrap' }}
          align="center"
          justify="center"
        >
          <InputGroup
            m={2}
            w="full"
            flexGrow={{ lg: 1 }}
            flexShrink={{ lg: 1 }}
            flexBasis={{ lg: '33.3333%' }}
          >
            <InputLeftElement pointerEvents="none" color="gray.500">
              <SearchIcon />
            </InputLeftElement>
            <Input
              placeholder="Enter doctor name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDownCapture={handleKeyPress}
              aria-label="Doctor Name"
            />
          </InputGroup>
          <InputGroup
            m={2}
            w="full"
            flexGrow={{ lg: 1 }}
            flexShrink={{ lg: 1 }}
            flexBasis={{ lg: '33.3333%' }}
          >
            <InputLeftElement pointerEvents="none" color="gray.500">
              <PhoneIcon />
            </InputLeftElement>
            <Input
              placeholder="Enter phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDownCapture={handleKeyPress}
              aria-label="Phone Number"
            />
          </InputGroup>
          <InputGroup
            m={2}
            w="full"
            flexGrow={{ lg: 1 }}
            flexShrink={{ lg: 1 }}
            flexBasis={{ lg: '33.3333%' }}
          >
            <InputLeftElement pointerEvents="none" color="gray.500">
              <AddIcon />
            </InputLeftElement>
            <Input
              placeholder="Enter practice name"
              value={practice}
              onChange={(e) => setPractice(e.target.value)}
              onKeyDownCapture={handleKeyPress}
              aria-label="Practice Name"
            />
          </InputGroup>
        </Flex>
        <Flex mt={4} justify={'center'}>
          <Tooltip
            label={`${
              resultsLoading
                ? 'Searching...'
                : 'Search for medical resources'
            }`}
          >
            <Button
              colorScheme="blue"
              onClick={handleSearch}
              type="submit"
              mb={4}
              isLoading={resultsLoading}
              loadingText="Searching"
              w={{ base: 'full', lg: 'auto' }}
              px={8}
            >
              Search
            </Button>
          </Tooltip>
        </Flex>
      </Box>
      {searchResults && Object.keys(searchResults).length > 0 && (
        <Flex alignItems={'center'} justify={'flex-end'} mb={4}>
          <Tooltip label="Table View" aria-label="Table View Tooltip">
            <Button
              colorScheme="blue"
              variant={isCardView ? 'outline' : 'solid'}
              onClick={toggleView}
              mr={4}
            >
              <PiTableBold />
            </Button>
          </Tooltip>
          <Tooltip label="Cards View" aria-label="Card View Tooltip">
            <Button
              colorScheme="blue"
              variant={isCardView ? 'solid' : 'outline'}
              onClick={toggleView}
            >
              <PiCardsBold />
            </Button>
          </Tooltip>
        </Flex>
      )}
      {searchAttempted && !searchResults && !resultsLoading ? (
        <Flex
          justify="center"
          alignItems="center"
          direction="column"
          mt={4}
          w="full"
        >
          <Box
            borderWidth="1px"
            borderRadius="lg"
            boxShadow="sm"
            p={[4, 6]}
            textAlign={'center'}
            bg="white"
            mx="2"
            maxW="md"
          >
            <WarningTwoIcon
              boxSize={[8, 10]}
              color="orange.400"
              mb={4}
            />
            <Heading size="lg" mb={2} color={'gray.800'}>
              No Results Found
            </Heading>
            <Text mb={4} color={'gray.600'}>
              We couldn't find anything matching your search criteria.
            </Text>
            <Text fontSize="sm" mb={4} color={'gray.500'}>
              {searchTips}
            </Text>
            <Button onClick={clearSearch}>Clear Search</Button>
          </Box>
        </Flex>
      ) : (
        searchResults &&
        Object.keys(searchResults).length > 0 &&
        (isCardView ? (
          <SearchResultsCardView
            searchResults={searchResults}
            setSearchResults={setSearchResults}
          />
        ) : (
          <SearchResultsTable
            searchResults={searchResults}
            setSearchResults={setSearchResults}
          />
        ))
      )}
    </>
  );
};

export default ResourceMatrixSearch;
